import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { 
  ArrowLeft, TrendingUp, Shield, AlertTriangle, CheckCircle2, 
  Building2, Globe, PieChart, DollarSign, Calendar, Users,
  Lightbulb, ExternalLink, BookOpen, Target, Percent, Scale,
  ChevronRight, Award, Info, BarChart3, Briefcase, Coins
} from 'lucide-react';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import Layout from '../components/Layout';
import { toast } from 'sonner';

// ETF Data for Tanzania
const TANZANIA_ETFS = [
  {
    id: 'vis-etf',
    name: 'VIS-ETF',
    fullName: 'Vertex International Securities ETF',
    manager: 'Vertex International Securities Limited',
    custodian: 'NBC Bank Limited',
    listed: 'October 16, 2025',
    exchange: 'DSE',
    minInvestment: 20000,
    minInvestmentFormatted: 'TZS 20,000',
    unitPrice: 1000,
    currentPrice: 1150,
    ytdReturn: 15.0,
    riskLevel: 'medium',
    riskScore: 3,
    description: {
      en: "Tanzania's first ETF, tracking the 5 best-performing companies on DSE. Provides diversified exposure to top Tanzanian equities.",
      sw: "ETF ya kwanza Tanzania, inafuatilia makampuni 5 bora zaidi ya DSE. Inatoa uwekezaji wa aina mbalimbali kwa hisa bora za Tanzania."
    },
    holdings: [
      { symbol: 'CRDB', name: 'CRDB Bank Plc', weight: 25, sector: 'Banking' },
      { symbol: 'NMB', name: 'NMB Bank Plc', weight: 25, sector: 'Banking' },
      { symbol: 'DSE', name: 'DSE Plc', weight: 20, sector: 'Financial Services' },
      { symbol: 'NICOL', name: 'NicholGreen Ltd', weight: 15, sector: 'Agriculture' },
      { symbol: 'AFRIPRISE', name: 'Afriprise Tanzania', weight: 15, sector: 'Diversified' }
    ],
    benefits: [
      { en: 'Instant diversification across top DSE stocks', sw: 'Usambazaji wa papo hapo kwa hisa bora za DSE' },
      { en: 'Low minimum investment (TZS 20,000)', sw: 'Uwekezaji wa chini (TZS 20,000)' },
      { en: 'Professional fund management', sw: 'Usimamizi wa kitaalamu wa mfuko' },
      { en: 'High liquidity - trade anytime on DSE', sw: 'Kimiminika cha juu - fanya biashara wakati wowote DSE' },
      { en: 'Transparent holdings and pricing', sw: 'Mali na bei zinazoonekana wazi' }
    ],
    risks: [
      { en: 'Market risk - value fluctuates with DSE performance', sw: 'Hatari ya soko - thamani inabadilika na utendaji wa DSE' },
      { en: 'Concentrated in 5 stocks only', sw: 'Imejikita katika hisa 5 pekee' },
      { en: 'Banking sector heavy (50% weight)', sw: 'Sekta ya benki nzito (uzito wa 50%)' }
    ],
    howToInvest: {
      en: 'Open a CDS account → Contact any DSE broker (Vertex, Orbit, Core) → Buy VIS-ETF units on DSE',
      sw: 'Fungua akaunti ya CDS → Wasiliana na dalali yeyote wa DSE (Vertex, Orbit, Core) → Nunua vitengo vya VIS-ETF kwenye DSE'
    },
    website: 'https://vertex.co.tz/vertex-etf/',
    color: '#10b981'
  },
  {
    id: 'ieaclc-etf',
    name: 'iTrust EAC Large Cap ETF',
    fullName: 'iTrust East Africa Community Large Cap ETF',
    manager: 'iTrust Finance Limited',
    custodian: 'NBC Bank (Tanzania), Absa (Kenya, Uganda)',
    listed: 'January 13, 2026',
    exchange: 'DSE',
    minInvestment: 100000,
    minInvestmentFormatted: 'TZS 100,000',
    unitPrice: 1000,
    currentPrice: 1250,
    ytdReturn: 25.0,
    riskLevel: 'medium',
    riskScore: 3,
    description: {
      en: "East Africa's first regional ETF, investing in large-cap stocks across Tanzania, Kenya, Uganda, and Rwanda. Provides regional diversification.",
      sw: "ETF ya kwanza ya kikanda Afrika Mashariki, inawekeza katika hisa kubwa nchini Tanzania, Kenya, Uganda, na Rwanda. Inatoa usambazaji wa kikanda."
    },
    holdings: [
      { symbol: 'CRDB', name: 'CRDB Bank (TZ)', weight: 12, sector: 'Banking' },
      { symbol: 'NMB', name: 'NMB Bank (TZ)', weight: 10, sector: 'Banking' },
      { symbol: 'SCOM', name: 'Safaricom (KE)', weight: 15, sector: 'Telecom' },
      { symbol: 'EQTY', name: 'Equity Group (KE)', weight: 12, sector: 'Banking' },
      { symbol: 'KCB', name: 'KCB Group (KE)', weight: 10, sector: 'Banking' },
      { symbol: 'VODA', name: 'Vodacom Tanzania', weight: 8, sector: 'Telecom' },
      { symbol: 'MTN', name: 'MTN Uganda', weight: 8, sector: 'Telecom' },
      { symbol: 'SBIC', name: 'Stanbic Uganda', weight: 7, sector: 'Banking' },
      { symbol: 'BK', name: 'Bank of Kigali (RW)', weight: 6, sector: 'Banking' },
      { symbol: 'OTHER', name: 'Other Holdings', weight: 12, sector: 'Various' }
    ],
    benefits: [
      { en: 'Regional diversification across 4 East African markets', sw: 'Usambazaji wa kikanda katika masoko 4 ya Afrika Mashariki' },
      { en: 'Access to Kenya\'s Safaricom and other blue chips', sw: 'Kufikia Safaricom ya Kenya na hisa nyingine bora' },
      { en: 'Professional active management', sw: 'Usimamizi hai wa kitaalamu' },
      { en: 'Strong IPO performance (540% oversubscribed)', sw: 'Utendaji mzuri wa IPO (ilizidi 540%)' },
      { en: 'High liquidity on DSE', sw: 'Kimiminika cha juu kwenye DSE' }
    ],
    risks: [
      { en: 'Currency risk from regional investments', sw: 'Hatari ya sarafu kutoka uwekezaji wa kikanda' },
      { en: 'Political/regulatory risk across multiple countries', sw: 'Hatari ya kisiasa/udhibiti katika nchi nyingi' },
      { en: 'Higher minimum investment (TZS 100,000)', sw: 'Uwekezaji wa chini wa juu (TZS 100,000)' }
    ],
    howToInvest: {
      en: 'Open a CDS account → Contact DSE broker → Buy IEACLC-ETF units on DSE or apply via iTrust website',
      sw: 'Fungua akaunti ya CDS → Wasiliana na dalali wa DSE → Nunua vitengo vya IEACLC-ETF kwenye DSE au omba kupitia tovuti ya iTrust'
    },
    website: 'https://www.itrust.co.tz',
    color: '#3b82f6'
  },
  {
    id: 'vbf',
    name: 'Vertex Bond Fund',
    fullName: 'Vertex Bond Fund (VBF)',
    manager: 'Vertex International Securities Limited',
    custodian: 'NBC Bank Limited',
    listed: '2024',
    exchange: 'OTC (Over-the-Counter)',
    minInvestment: 500,
    minInvestmentFormatted: 'TZS 500',
    unitPrice: null,
    currentPrice: null,
    ytdReturn: 14.5,
    riskLevel: 'low',
    riskScore: 2,
    description: {
      en: "A fixed-income fund investing in Tanzanian government bonds. Offers stable returns with lower risk than equities. Perfect for conservative investors.",
      sw: "Mfuko wa mapato ya kudumu unaowekeza katika hatifungani za serikali ya Tanzania. Inatoa faida thabiti na hatari ndogo kuliko hisa. Bora kwa wawekezaji waangalifu."
    },
    holdings: [
      { symbol: 'TBOND-2Y', name: '2-Year Treasury Bond', weight: 15, sector: 'Government' },
      { symbol: 'TBOND-5Y', name: '5-Year Treasury Bond', weight: 20, sector: 'Government' },
      { symbol: 'TBOND-10Y', name: '10-Year Treasury Bond', weight: 25, sector: 'Government' },
      { symbol: 'TBOND-15Y', name: '15-Year Treasury Bond', weight: 20, sector: 'Government' },
      { symbol: 'TBOND-25Y', name: '25-Year Treasury Bond', weight: 20, sector: 'Government' }
    ],
    benefits: [
      { en: 'Very low minimum (TZS 500) - most accessible fund', sw: 'Chini sana (TZS 500) - mfuko unaopatikana zaidi' },
      { en: 'Stable 14%+ annual returns', sw: 'Faida thabiti ya 14%+ kwa mwaka' },
      { en: 'Government-backed securities - very safe', sw: 'Dhamana za serikali - salama sana' },
      { en: 'Flexible withdrawals (TZS 100,000+)', sw: 'Uondoaji rahisi (TZS 100,000+)' },
      { en: 'No stock market volatility', sw: 'Hakuna msongo wa soko la hisa' }
    ],
    risks: [
      { en: 'Lower returns than equities/ETFs', sw: 'Faida ndogo kuliko hisa/ETF' },
      { en: 'Interest rate risk - bond prices fall when rates rise', sw: 'Hatari ya riba - bei za hatifungani hushuka riba zinapopanda' },
      { en: 'Inflation risk - may not beat inflation in some years', sw: 'Hatari ya mfumuko - huenda isishinde mfumuko miaka mingine' }
    ],
    howToInvest: {
      en: 'Contact Vertex directly → Open investment account → Deposit as little as TZS 500 → Start earning',
      sw: 'Wasiliana na Vertex moja kwa moja → Fungua akaunti ya uwekezaji → Weka hata TZS 500 → Anza kupata faida'
    },
    website: 'https://vertex.co.tz/vertex-bond-fund/',
    color: '#f59e0b'
  }
];

// Brokers who can help invest
const BROKERS = [
  {
    name: 'Vertex International Securities',
    phone: '+255 22 211 6513',
    email: 'info@vertex.co.tz',
    website: 'https://vertex.co.tz',
    services: ['VIS-ETF', 'Vertex Bond Fund', 'DSE Stocks', 'Bonds']
  },
  {
    name: 'Orbit Securities',
    phone: '+255 22 211 7100',
    email: 'info@orbitsecurities.co.tz',
    website: 'https://orbitsecurities.co.tz',
    services: ['All DSE ETFs', 'DSE Stocks', 'Bonds']
  },
  {
    name: 'Core Securities',
    phone: '+255 22 211 2891',
    email: 'info@coresecurities.co.tz',
    website: 'https://coresecurities.co.tz',
    services: ['All DSE ETFs', 'DSE Stocks', 'Bonds']
  },
  {
    name: 'iTrust Finance',
    phone: '+255 22 292 5821',
    email: 'info@itrust.co.tz',
    website: 'https://www.itrust.co.tz',
    services: ['iTrust EAC ETF', 'Fund Management']
  }
];

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16'];

export default function ETFEducation() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const [selectedETF, setSelectedETF] = useState(TANZANIA_ETFS[0]);
  const [activeTab, setActiveTab] = useState('overview');

  const getLocalizedText = (textObj) => {
    if (typeof textObj === 'string') return textObj;
    return language === 'sw' ? textObj.sw : textObj.en;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-TZ').format(amount);
  };

  const getRiskColor = (level) => {
    switch(level) {
      case 'low': return 'bg-green-100 text-green-700';
      case 'medium': return 'bg-amber-100 text-amber-700';
      case 'high': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getRiskLabel = (level) => {
    const labels = {
      low: { en: 'Low Risk', sw: 'Hatari Ndogo' },
      medium: { en: 'Medium Risk', sw: 'Hatari ya Kati' },
      high: { en: 'High Risk', sw: 'Hatari Kubwa' }
    };
    return getLocalizedText(labels[level] || labels.medium);
  };

  // Prepare sector allocation data for pie chart
  const sectorData = selectedETF.holdings.reduce((acc, holding) => {
    const existing = acc.find(s => s.name === holding.sector);
    if (existing) {
      existing.value += holding.weight;
    } else {
      acc.push({ name: holding.sector, value: holding.weight });
    }
    return acc;
  }, []);

  return (
    <Layout>
      <div className="page-enter space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="etf-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-primary" />
              {t('ETFs & Investment Funds', 'ETF na Mifuko ya Uwekezaji')}
            </h1>
            <p className="text-muted-foreground text-sm">
              {t('Learn about Tanzania\'s ETFs and how to invest', 'Jifunze kuhusu ETF za Tanzania na jinsi ya kuwekeza')}
            </p>
          </div>
        </div>

        {/* What is an ETF - Education Box */}
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Lightbulb className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">
                  {t('What is an ETF?', 'ETF ni nini?')}
                </h3>
                <p className="text-sm text-blue-800">
                  {t(
                    'An Exchange Traded Fund (ETF) is a basket of investments (stocks, bonds) that trades on a stock exchange like a single stock. Instead of buying many stocks individually, you buy one ETF and instantly own pieces of multiple companies. It\'s like buying a "sampler pack" of investments!',
                    'Exchange Traded Fund (ETF) ni mkusanyiko wa uwekezaji (hisa, hatifungani) unaofanya biashara kwenye soko la hisa kama hisa moja. Badala ya kununua hisa nyingi moja moja, ununua ETF moja na mara moja kumiliki vipande vya makampuni mengi. Ni kama kununua "pakiti ya sampuli" ya uwekezaji!'
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ETF Selection Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {TANZANIA_ETFS.map((etf) => (
            <Card 
              key={etf.id}
              className={`cursor-pointer transition-all hover:shadow-md ${selectedETF.id === etf.id ? 'ring-2 ring-primary' : ''}`}
              onClick={() => setSelectedETF(etf)}
              data-testid={`etf-card-${etf.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Badge style={{ backgroundColor: etf.color }} className="text-white">
                    {etf.name}
                  </Badge>
                  <Badge className={getRiskColor(etf.riskLevel)}>
                    {getRiskLabel(etf.riskLevel)}
                  </Badge>
                </div>
                <h3 className="font-semibold text-sm mb-1">{etf.fullName}</h3>
                <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
                  {getLocalizedText(etf.description)}
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-muted-foreground">{t('Min:', 'Chini:')}</span>
                    <span className="font-medium ml-1">{etf.minInvestmentFormatted}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{t('Return:', 'Faida:')}</span>
                    <span className="font-medium text-green-600 ml-1">+{etf.ytdReturn}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Selected ETF Details */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: selectedETF.color }}></div>
                  {selectedETF.fullName}
                </CardTitle>
                <CardDescription>{t('Managed by', 'Inasimamiwa na')} {selectedETF.manager}</CardDescription>
              </div>
              <Button variant="outline" size="sm" asChild>
                <a href={selectedETF.website} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  {t('Official Website', 'Tovuti Rasmi')}
                </a>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="overview">{t('Overview', 'Muhtasari')}</TabsTrigger>
                <TabsTrigger value="holdings">{t('Holdings', 'Mali')}</TabsTrigger>
                <TabsTrigger value="benefits">{t('Benefits', 'Faida')}</TabsTrigger>
                <TabsTrigger value="risks">{t('Risks', 'Hatari')}</TabsTrigger>
                <TabsTrigger value="invest">{t('How to Invest', 'Jinsi ya Kuwekeza')}</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                      <Calendar className="w-3 h-3" />
                      {t('Listed', 'Imeorodheshwa')}
                    </div>
                    <p className="font-semibold">{selectedETF.listed}</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                      <Building2 className="w-3 h-3" />
                      {t('Exchange', 'Soko')}
                    </div>
                    <p className="font-semibold">{selectedETF.exchange}</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                      <DollarSign className="w-3 h-3" />
                      {t('Min Investment', 'Uwekezaji wa Chini')}
                    </div>
                    <p className="font-semibold">{selectedETF.minInvestmentFormatted}</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                      <TrendingUp className="w-3 h-3" />
                      {t('YTD Return', 'Faida ya Mwaka')}
                    </div>
                    <p className="font-semibold text-green-600">+{selectedETF.ytdReturn}%</p>
                  </div>
                </div>

                <div className="p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-medium mb-2">{t('About this Fund', 'Kuhusu Mfuko Huu')}</h4>
                  <p className="text-sm text-muted-foreground">
                    {getLocalizedText(selectedETF.description)}
                  </p>
                </div>

                {/* Risk Meter */}
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Scale className="w-4 h-4" />
                    {t('Risk Level', 'Kiwango cha Hatari')}
                  </h4>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex justify-between text-xs text-muted-foreground mb-1">
                        <span>{t('Low', 'Chini')}</span>
                        <span>{t('Medium', 'Kati')}</span>
                        <span>{t('High', 'Juu')}</span>
                      </div>
                      <div className="h-3 bg-gradient-to-r from-green-400 via-amber-400 to-red-400 rounded-full relative">
                        <div 
                          className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-2 border-gray-800 rounded-full shadow"
                          style={{ left: `${(selectedETF.riskScore / 5) * 100}%`, transform: 'translate(-50%, -50%)' }}
                        ></div>
                      </div>
                    </div>
                    <Badge className={getRiskColor(selectedETF.riskLevel)}>
                      {getRiskLabel(selectedETF.riskLevel)}
                    </Badge>
                  </div>
                </div>
              </TabsContent>

              {/* Holdings Tab */}
              <TabsContent value="holdings" className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Holdings List */}
                  <div>
                    <h4 className="font-medium mb-3">{t('What This Fund Owns', 'Mfuko Huu Unamiliki Nini')}</h4>
                    <div className="space-y-2">
                      {selectedETF.holdings.map((holding, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-2 h-8 rounded"
                              style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                            ></div>
                            <div>
                              <p className="font-medium text-sm">{holding.symbol}</p>
                              <p className="text-xs text-muted-foreground">{holding.name}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">{holding.weight}%</p>
                            <Badge variant="outline" className="text-xs">{holding.sector}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Sector Pie Chart */}
                  <div>
                    <h4 className="font-medium mb-3">{t('Sector Allocation', 'Ugawaji wa Sekta')}</h4>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPie>
                          <Pie
                            data={sectorData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, value }) => `${name}: ${value}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {sectorData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </RechartsPie>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Benefits Tab */}
              <TabsContent value="benefits">
                <div className="space-y-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    {t('Why Invest in This Fund?', 'Kwa Nini Uwekeze Katika Mfuko Huu?')}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selectedETF.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-100">
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-green-800">{getLocalizedText(benefit)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Risks Tab */}
              <TabsContent value="risks">
                <div className="space-y-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    {t('Risks to Consider', 'Hatari za Kuzingatia')}
                  </h4>
                  <div className="space-y-3">
                    {selectedETF.risks.map((risk, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
                        <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-800">{getLocalizedText(risk)}</p>
                      </div>
                    ))}
                  </div>

                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-100 mt-4">
                    <div className="flex items-start gap-2">
                      <Info className="w-5 h-5 text-blue-500 flex-shrink-0" />
                      <p className="text-sm text-blue-800">
                        {t(
                          'Remember: All investments carry risk. Never invest money you cannot afford to lose. Consider your risk tolerance and investment goals before investing.',
                          'Kumbuka: Uwekezaji wote una hatari. Usiwekeze pesa ambazo huwezi kumudu kupoteza. Zingatia uvumilivu wako wa hatari na malengo ya uwekezaji kabla ya kuwekeza.'
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* How to Invest Tab */}
              <TabsContent value="invest">
                <div className="space-y-4">
                  <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Target className="w-5 h-5 text-primary" />
                      {t('Steps to Invest', 'Hatua za Kuwekeza')}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {getLocalizedText(selectedETF.howToInvest)}
                    </p>
                  </div>

                  <h4 className="font-medium">{t('Licensed Brokers Who Can Help', 'Madalali Wenye Leseni Wanaoweza Kusaidia')}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {BROKERS.filter(b => b.services.some(s => 
                      s.includes(selectedETF.name) || s.includes('All DSE') || s.includes('DSE Stocks')
                    )).map((broker, idx) => (
                      <Card key={idx}>
                        <CardContent className="p-4">
                          <h5 className="font-semibold mb-2">{broker.name}</h5>
                          <div className="space-y-1 text-sm text-muted-foreground">
                            <p>📞 {broker.phone}</p>
                            <p>✉️ {broker.email}</p>
                            <a 
                              href={broker.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-primary hover:underline flex items-center gap-1"
                            >
                              🌐 {t('Visit Website', 'Tembelea Tovuti')}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {broker.services.slice(0, 3).map((service, sidx) => (
                              <Badge key={sidx} variant="outline" className="text-xs">{service}</Badge>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  <Button 
                    className="w-full" 
                    size="lg"
                    onClick={() => {
                      toast.success(t(
                        'Contact a broker above to start investing!',
                        'Wasiliana na dalali hapo juu kuanza kuwekeza!'
                      ));
                    }}
                    data-testid="start-investing-btn"
                  >
                    <Briefcase className="w-4 h-4 mr-2" />
                    {t('I Want to Invest in', 'Nataka Kuwekeza katika')} {selectedETF.name}
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Comparison Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Scale className="w-5 h-5" />
              {t('Quick Comparison', 'Ulinganisho wa Haraka')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">{t('Feature', 'Kipengele')}</th>
                    {TANZANIA_ETFS.map(etf => (
                      <th key={etf.id} className="text-center p-2">
                        <Badge style={{ backgroundColor: etf.color }} className="text-white">{etf.name}</Badge>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-2 font-medium">{t('Min Investment', 'Uwekezaji wa Chini')}</td>
                    {TANZANIA_ETFS.map(etf => (
                      <td key={etf.id} className="text-center p-2">{etf.minInvestmentFormatted}</td>
                    ))}
                  </tr>
                  <tr className="border-b">
                    <td className="p-2 font-medium">{t('YTD Return', 'Faida ya Mwaka')}</td>
                    {TANZANIA_ETFS.map(etf => (
                      <td key={etf.id} className="text-center p-2 text-green-600 font-semibold">+{etf.ytdReturn}%</td>
                    ))}
                  </tr>
                  <tr className="border-b">
                    <td className="p-2 font-medium">{t('Risk Level', 'Kiwango cha Hatari')}</td>
                    {TANZANIA_ETFS.map(etf => (
                      <td key={etf.id} className="text-center p-2">
                        <Badge className={getRiskColor(etf.riskLevel)}>{getRiskLabel(etf.riskLevel)}</Badge>
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b">
                    <td className="p-2 font-medium">{t('Exchange', 'Soko')}</td>
                    {TANZANIA_ETFS.map(etf => (
                      <td key={etf.id} className="text-center p-2">{etf.exchange}</td>
                    ))}
                  </tr>
                  <tr>
                    <td className="p-2 font-medium">{t('Best For', 'Bora Kwa')}</td>
                    <td className="text-center p-2 text-xs">{t('DSE-focused investors', 'Wawekezaji wa DSE')}</td>
                    <td className="text-center p-2 text-xs">{t('Regional diversification', 'Usambazaji wa kikanda')}</td>
                    <td className="text-center p-2 text-xs">{t('Conservative/beginners', 'Waangalifu/wanaoanza')}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
          <CardContent className="p-6 text-center">
            <Award className="w-12 h-12 text-primary mx-auto mb-3" />
            <h3 className="text-xl font-bold mb-2">
              {t('Ready to Start Your ETF Journey?', 'Uko Tayari Kuanza Safari Yako ya ETF?')}
            </h3>
            <p className="text-muted-foreground mb-4">
              {t(
                'ETFs are one of the easiest ways to start investing. With as little as TZS 500, you can begin building wealth.',
                'ETF ni moja ya njia rahisi zaidi za kuanza kuwekeza. Kwa TZS 500 tu, unaweza kuanza kujenga utajiri.'
              )}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button onClick={() => navigate('/chat')} data-testid="ask-ai-btn">
                <Lightbulb className="w-4 h-4 mr-2" />
                {t('Ask AI Coach', 'Uliza Kocha wa AI')}
              </Button>
              <Button variant="outline" onClick={() => navigate('/simulator')}>
                <BarChart3 className="w-4 h-4 mr-2" />
                {t('Try Simulator', 'Jaribu Kihesabu')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
